/**
 * @quotech/auth - Unified Auth Store
 *
 * SINGLE AUTH STORE FOR ALL QUOTECH MODULES
 *
 * Storage key: 'quotech-auth'
 *
 * Features:
 * - Zustand with persist middleware
 * - Dual-read from legacy keys on initialization
 * - SSO token handling
 * - Token refresh support
 * - Organisation context
 */

import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { AuthState, User, TokenPair } from './types'
import { migrateLegacyAuth } from './legacyBridge'
import {
  setAuthCookie, setRefreshCookie, setOrgCookie,
  getAuthCookie, getRefreshCookie, getOrgCookie,
  clearAllAuthCookies, isTokenExpired,
} from './cookieAuth'
import { performSetFromSSO, performGetFreshTokenForSSO, performCheckAuth } from './auth-store/authActions'
import { broadcastIdentityChange } from './session-manager/tokenSync'
import { tokenMatchesUser } from './identityReconcile'

const STORAGE_KEY = 'quotech-auth'

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      accessToken: null,
      refreshToken: null,
      organisationId: null,
      organisationName: null,
      isAuthenticated: false,
      isLoading: true,
      error: null,

      // Set full auth state (also sets shared domain cookies)
      setAuth: (tokens: TokenPair, user: User) => {
        const previousUserId = get().user?.id ?? get().user?.email ?? null
        setAuthCookie(tokens.access)
        if (tokens.refresh) setRefreshCookie(tokens.refresh)
        if (user.organisation_id) setOrgCookie(user.organisation_id, user.organisation_name || undefined)
        set({
          user, accessToken: tokens.access, refreshToken: tokens.refresh,
          organisationId: user.organisation_id || null,
          organisationName: user.organisation_name || null,
          isAuthenticated: true, isLoading: false, error: null,
        })
        // Cross-tab identity-change broadcast. Fires only when the new
        // user differs from the previously-known user (avoids spamming
        // sibling tabs on token refresh writes that don't change
        // identity). See BUG-2026-05-21-CROSS-TAB-IDENTITY-DRIFT.
        const newUserId = user.id ?? user.email ?? null
        if (previousUserId !== newUserId) {
          broadcastIdentityChange({
            userId: newUserId,
            orgId: user.organisation_id ?? null,
            reason: 'useAuthStore.setAuth',
          })
        }
      },

      setFromSSO: (token, orgId, orgName) => performSetFromSSO(token, orgId, orgName, get, set),

      updateTokens: (access: string, refresh?: string) => {
        setAuthCookie(access)
        if (refresh) setRefreshCookie(refresh)
        set({ accessToken: access, ...(refresh && { refreshToken: refresh }) })
      },

      updateUser: (userData: Partial<User>) => {
        const current = get().user
        if (current) {
          set({
            user: { ...current, ...userData },
            ...(userData.organisation_id && { organisationId: userData.organisation_id }),
            ...(userData.organisation_name && { organisationName: userData.organisation_name }),
          })
        }
      },

      clearAuth: () => {
        clearAllAuthCookies()
        if (typeof localStorage !== 'undefined') {
          // Clear legacy Redux-era keys so the main dashboard's auto-login
          // doesn't re-authenticate using stale data
          const LEGACY_KEYS = [
            'UserData', 'IdToken', 'refreshToken', 'org_id',
            'CurrentTenantId', 'CurrentTenantName', 'org_name',
            'ProfileData', 'accessTokennew', 'quotech_sso_email',
          ]
          for (const key of LEGACY_KEYS) localStorage.removeItem(key)

          const LOGOUT_EVENT_KEY = 'quotech:logout'
          localStorage.setItem(LOGOUT_EVENT_KEY, Date.now().toString())
          localStorage.removeItem(LOGOUT_EVENT_KEY)
        }
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('quotech:logout'))
        }
        set({
          user: null, accessToken: null, refreshToken: null,
          organisationId: null, organisationName: null,
          isAuthenticated: false, isLoading: false, error: null,
        })
      },

      getFreshTokenForSSO: () => performGetFreshTokenForSSO(get, set),

      checkAuth: () => performCheckAuth(get, set),

      setLoading: (loading: boolean) => set({ isLoading: loading }),
      setError: (error: string | null) => set({ error }),
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        organisationId: state.organisationId,
        organisationName: state.organisationName,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        if (!state) return

        const cookieToken = getAuthCookie()
        const cookieRefresh = getRefreshCookie()
        const cookieOrg = getOrgCookie()

        if (cookieToken && (!state.isAuthenticated || !state.accessToken)) {
          console.log('[@quotech/auth] Using shared cookie auth')
          state.accessToken = cookieToken
          state.refreshToken = cookieRefresh || state.refreshToken
          state.organisationId = cookieOrg?.id || state.organisationId
          state.organisationName = cookieOrg?.name || state.organisationName
          state.isAuthenticated = true
          // Cold-load identity-adoption guard (BUG-2026-06-02): we are
          // adopting the shared cookie token, but the PERSISTED user may
          // belong to a different account (e.g. a stale dev@quotech.ai
          // record). If the token's `sub` doesn't match the persisted
          // user, drop it now so the brief window before checkAuth()
          // re-fetches the real profile can't render the wrong identity.
          // reconcileIdentity is pure; `indeterminate` keeps the user.
          if (tokenMatchesUser(cookieToken, state.user) === 'mismatch') {
            console.warn('[@quotech/auth] Rehydrate: persisted user does not match cookie token — clearing stale user')
            state.user = null
          }
        } else if (!state.isAuthenticated || !state.accessToken) {
          const legacy = migrateLegacyAuth()
          if (legacy) {
            console.log(`[@quotech/auth] Migrating auth from legacy: ${legacy.source}`)
            state.accessToken = legacy.tokens.access
            state.refreshToken = legacy.tokens.refresh
            state.user = legacy.user
            state.organisationId = legacy.orgId
            state.organisationName = legacy.orgName
            state.isAuthenticated = true
            if (legacy.tokens.access) setAuthCookie(legacy.tokens.access)
            if (legacy.tokens.refresh) setRefreshCookie(legacy.tokens.refresh)
            if (legacy.orgId) setOrgCookie(legacy.orgId, legacy.orgName || undefined)
          }
        } else if (state.isAuthenticated && state.accessToken && !cookieToken) {
          // DURABILITY (BUG-2026-06-30-STALE-TOKEN-RESURRECTION): only
          // re-mint the shared cookie from the PERSISTED store token when
          // that token is still LIVE. Writing an EXPIRED persisted token
          // back into the .quotech.ai jar resurrects a dead session — the
          // app then 401-storms on every refresh and bounces login. When
          // it's expired we leave the cookie absent and defer to
          // SessionManager's silent refresh (HttpOnly quotech_refresh) /
          // v1 refresh, which is the authoritative stay-logged-in path:
          // it re-mints a fresh cookie or cleanly signs the user out. We
          // deliberately keep `isAuthenticated` as-is here (no synchronous
          // flip to logged-out) so a legitimate just-expired session does
          // not flash the login screen before the silent refresh resolves.
          if (!isTokenExpired(state.accessToken)) {
            console.log('[@quotech/auth] Syncing store auth to shared cookie')
            setAuthCookie(state.accessToken)
            if (state.refreshToken) setRefreshCookie(state.refreshToken)
            if (state.organisationId) setOrgCookie(state.organisationId, state.organisationName || undefined)
          } else {
            console.warn('[@quotech/auth] Rehydrate: persisted access token expired with no live cookie — not resurrecting jar, deferring to silent refresh')
          }
        }

        state.isLoading = false
      },
    }
  )
)

export default useAuthStore
